import { apiJson, getApiBaseUrl } from "../../../shared/lib/api-client.ts";
import { filtersToQueryParams, type LeaderboardFilters } from "../types/filters.ts";
import type { ApiResponse, Category, Project } from "../types/index.ts";

export interface ValidateProjectResponse {
  valid: boolean;
  handle?: string;
  error?: string;
  isCryptoProject?: boolean | null;
  moniData?: {
    rawPayload: unknown;
    moniScore: number;
    smartsCount: number;
    smartMentionsCount: number;
    smartTier: number;
  } | null;
  existing?: {
    id: string;
    name: string | null;
    score: number | null;
    confidence: number | null;
    lastAuditedAt: string | null;
  } | null;
}

export interface SubmitProjectPayload {
  twitterHandle: string;
  forceRescore?: boolean;
  moniData?: unknown;
}

export async function getCategories(): Promise<Category[]> {
  const payload = await apiJson<ApiResponse<Category[]>>("/v1/categories");
  return payload.data ?? [];
}

export interface ProjectsResponse {
  data: Project[];
  pagination: { limit: number; offset: number; total: number };
}

export async function getProjects(
  params: {
    limit?: number;
    offset?: number;
    sort?: string;
    direction?: "asc" | "desc";
    filters?: LeaderboardFilters;
  } = {}
): Promise<ProjectsResponse> {
  const { limit = 50, offset = 0, sort, direction, filters = {} } = params;
  const url = new URL("/v1/projects", getApiBaseUrl());
  url.searchParams.set("limit", String(limit));
  url.searchParams.set("offset", String(offset));
  if (sort && sort !== "total") {
    url.searchParams.set("sort", sort);
  }
  if (direction && direction !== "desc") {
    url.searchParams.set("direction", direction);
  }
  for (const [k, v] of Object.entries(filtersToQueryParams(filters))) {
    url.searchParams.set(k, v);
  }

  return apiJson<ProjectsResponse>(url.toString());
}

export async function getProjectById(projectId: string): Promise<Project | null> {
  const payload = await apiJson<ApiResponse<Project>>(`/v1/projects/${projectId}?trends=false`);
  return payload.data ?? null;
}

export async function validateProjectHandle(handle: string): Promise<ValidateProjectResponse> {
  return apiJson<ValidateProjectResponse>("/v1/projects/validate", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ handle }),
  });
}

export async function submitProject(
  payload: SubmitProjectPayload
): Promise<Record<string, unknown>> {
  return apiJson<Record<string, unknown>>("/v1/projects", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(payload),
  });
}

export async function confirmProjectTge(
  projectId: string,
  params: {
    tgeDate: string;
    /** Required — tge_outcomes.gecko_id is NOT NULL and the daily
     *  price-checkpoint + rescore pipeline depends on a CoinGecko
     *  identifier to operate. Enforced at both Zod (backend) and the
     *  modal submit button (frontend) as of 2026-04-22. */
    geckoId: string;
    tokenSymbol?: string;
    tokenName?: string;
    note?: string;
  }
): Promise<{ success: boolean }> {
  return apiJson(`/v1/admin/projects/${projectId}/confirm-tge`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(params),
  });
}
